
import type { DataTablePaginationState } from '@neo/blocks/cx-data-table'
import { createStore } from '@shared/utils/state/state-factory'
import { updateUnreadCountsForConversation } from 'v2/pages/conversations/utils'
import { type ConversationMeta, ConversationsViewType } from 'v2/types'
import type {
  FiltersType,
  IFilterChild,
  IFilterGroup,
  ViewRecordsWithPagination,
} from 'v2/types/api/conversations'

interface ConversationStore {
  currentView: ConversationsViewType
  viewUUID: string
  setViewUUID: (uuid: string) => void
}

interface SharedConversationStore {
  // Add shared state properties here
  lastReadTimestamp: Record<string, number>
  setLastReadTimestamp: (conversationNumber: string, timestamp: number) => void
  unreadCounts: Record<string, number>
  setUnreadCounts: (conversationNumber: string, count: number) => void
  clearUnreadCounts: (conversationNumber: string) => void
  meta: Record<string, ConversationMeta>
  setMeta: (conversationNumber: string, meta: Partial<ConversationMeta>) => void
}

interface Actions {
  setCurrentView: (view: ConversationsViewType) => void
  viewFilters: Record<string, ViewRecordsWithPagination | never>
  setViewTableViewParams: (uuid: string, viewParams?: ViewRecordsWithPagination) => void
  setViewTablePagination: (uuid: string, pagination?: DataTablePaginationState) => void
  setViewTableSearchQuery: (uuid: string, searchQuery?: string) => void
  setViewTableFilters: (uuid: string, filters?: FiltersType | Record<string, never>) => void
  setViewTableColumns: (uuid: string, fields?: string[]) => void
}

export const defaultFilterChild: IFilterChild = {
  op_type: '',
  type: '',
  value: '',
}

export const initialFilterState = {
  filters: {
    filter_groups: [
      {
        filter_children: [defaultFilterChild],
        filter_conditions: [],
        group_child: false,
      },
    ] as IFilterGroup[],
    group_conditions: [],
  },
}

export const useConversationStore = createStore<ConversationStore & Actions>(
  'conversation',
  (set) => ({
    viewFilters: {},
    currentView: ConversationsViewType.table,
    viewUUID: '',
    setViewUUID: (uuid) =>
      set((state) => {
        state.viewUUID = uuid
      }),
    setCurrentView: (view) =>
      set((state) => {
        state.currentView = view
      }),
    setViewTableViewParams: (uuid, filter) =>
      set((state) => {
        if (filter && uuid) {
          state.viewFilters[uuid] = filter
        }
      }),
    setViewTablePagination: (uuid, pagination) => {
      set((state) => {
        if (uuid) {
          const filter = state.viewFilters[uuid]
          if (filter && pagination) {
            filter.pagination = pagination
          }
        }
      })
    },
    setViewTableSearchQuery: (uuid, searchQuery) => {
      set((state) => {
        if (uuid && state.viewFilters[uuid]) {
          state.viewFilters[uuid].search_query = searchQuery ?? ''
          state.viewFilters[uuid].pagination = {
            pageIndex: 0,
            pageSize: 50,
          }
        }
      })
    },
    setViewTableFilters: (uuid, filters = {}) => {
      set((state) => {
        if (uuid && state.viewFilters[uuid] && filters) {
          state.viewFilters[uuid].filters = filters
          state.viewFilters[uuid].pagination = {
            pageIndex: 0,
            pageSize: 50,
          }
        }
      })
    },
    setViewTableColumns: (uuid, fields) => {
      set((state) => {
        if (uuid && state.viewFilters[uuid] && fields) {
          state.viewFilters[uuid].fields = fields
        }
      })
    },
  }),
  {
    persist: true,
  },
)

export const useSharedConversationStore = createStore<SharedConversationStore>(
  'shared-conversation',
  (set) => ({
    lastReadTimestamp: {},
    unreadCounts: {},
    meta: {},
    setMeta: (conversationNumber, meta) =>
      set((state) => {
        state.meta[conversationNumber] = { ...state.meta[conversationNumber], ...meta }
        if (meta.error?.timeout) {
          setTimeout(() => {
            useSharedConversationStore.getState().setMeta(conversationNumber, {
              error: null,
            })
          }, meta.error.timeout)
        }
      }),
    setLastReadTimestamp: (conversationNumber, timestamp) =>
      set((state) => {
        state.lastReadTimestamp[conversationNumber] = timestamp
        updateUnreadCountsForConversation(conversationNumber)
      }),
    setUnreadCounts: (conversationNumber, count) =>
      set((state) => {
        state.unreadCounts[conversationNumber] = count
      }),
    clearUnreadCounts: (conversationNumber) =>
      set((state) => {
        state.unreadCounts[conversationNumber] = 0
        state.lastReadTimestamp[conversationNumber] = Date.now()
      }),
  }),
  {
    shared: true,
    persist: true,
  },
)
